import {
  Button,
  InputField,
  DropDownInput,
  AddressInputField,
} from "@/components";
import { statesOptionsArray, getStateKey } from "../../../utils/Data";
import { useState, useCallback, useEffect, useMemo } from "react";
import { FaArrowRight } from "react-icons/fa6";
import { SheetClose } from "@/components/ui/sheet";
import { useDispatch } from "react-redux";
import {
  savePickupAddress,
  updatePickupAddress,
} from "../../../app/slices/pickupAddressSlice";

import { PickupAddressSchema } from "../../../schemas";
import { useValidation, useFieldError, useAlert } from "@/";

const PickupAddressForm = ({
  setSheetOpen = false,
  data = {},
  isEdit = false,
}) => {
  console.log("formData:::", data, isEdit);
  const { alert, AlertDialog } = useAlert();
  const [formData, setFormData] = useState({
    contactPerson: data.contactPerson || "",
    mobileNo: data.contactPhone || "",
    pincode: data.pincode || "",
    city: data.city || "",
    selectedState: data.state || "",
    pickupAddress: data.lines?.[0]?.line1 || "",
    landmark: data.landmark || "",
    gstNo: data.gstNumber || "",
  });

  const [isLoadingPincode, setIsLoadingPincode] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { validate } = useValidation(PickupAddressSchema);
  const [fieldErrors, setFieldErrors] = useState([]);
  const getErrorMessage = useFieldError(fieldErrors);
  const [generalError, setGeneralError] = useState("");
  const dispatch = useDispatch();
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingPincode, setLoadingPincode] = useState(false);

  // Helper function to get validation error or field error
  const getFieldError = (fieldName) => {
    return validationErrors[fieldName] || getErrorMessage(fieldName);
  };

  // Handle form submission
  const handleSubmit = async () => {
    setFieldErrors([]);
    setGeneralError("");

    const body = {
      addressType: "Pickup",
      lines: [{ line1: formData.pickupAddress, line2: "" }],
      city: formData.city,
      state: formData.selectedState,
      pincode: formData.pincode,
      landmark: formData.landmark,
      isDefault: true,
      contactPerson: formData.contactPerson,
      contactPhone: formData.mobileNo,
      gstNumber: formData.gstNo,
    };

    const validation = validate(body);
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      return;
    }

    try {
      console.log("📤 Sending Data to savePickupAddress:", body);

      const response = await dispatch(savePickupAddress(validation.data));

      if (savePickupAddress.fulfilled.match(response)) {
        console.log("✅ Pickup Address Saved Successfully:", response.payload);
        await alert("✅ Pickup Address Saved Successfully");
        setSheetOpen(false);
      } else if (savePickupAddress.rejected.match(response)) {
        const errorMsg = response.payload || "Failed to save pickup address.";
        console.error("❌ Save Failed:", errorMsg);
        alert("❌ Save Failed");
        setGeneralError(errorMsg);
      }
    } catch ({ error }) {
      console.error("❌ Unexpected Error:", error.message || error);
      alert("❌ Save Failed");
      setGeneralError(error.message || "An unexpected error occurred.");
    }
  };
  const handleEdit = async () => {
    setFieldErrors([]);
    setGeneralError("");

    const body = {
      addressType: "Pickup",
      lines: [{ line1: formData.pickupAddress, line2: "" }],
      city: formData.city,
      state: formData.selectedState,
      pincode: formData.pincode.toString(),
      landmark: formData.landmark,
      isDefault: true,
      contactPerson: formData.contactPerson,
      contactPhone: formData.mobileNo,
      gstNumber: formData.gstNo,
    };

    const validation = validate(body);
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      return;
    }

    try {
      console.log("📤 Sending Data to editedPickupAddress:", body);

      const response = await dispatch(
        updatePickupAddress({
          data: validation.data,
          id: data.addressId,
        })
      );

      if (savePickupAddress.fulfilled.match(response)) {
        console.log(
          "✅ Pickup Address Updated Successfully:",
          response.payload
        );
        await alert("✅ Pickup Address Updated Successfully");
        setSheetOpen(false);
      } else if (updatePickupAddress.rejected.match(response)) {
        const errorMsg = response.payload || "Failed to update pickup address.";
        console.error("❌ Update Failed:", errorMsg);
        alert("❌ Update Failed");
        setGeneralError(errorMsg);
      }
    } catch ({ error }) {
      console.error("❌ Unexpected Error:", error.message || error);
      alert("❌ Update Failed");
      setGeneralError(error.message || "An unexpected error occurred.");
    }
  };
  // Handle input changes
  const handleChange = useCallback(
    (key, value) => {
      // Clear any existing validation error for this field
      setValidationErrors((prev) => ({ ...prev, [key]: null }));

      // Validate address fields to prevent special characters
      if (key === "pickupAddress" || key === "landmark" || key === "city") {
        // Check for special characters
        const specialCharsRegex = /[&@#$%^*+={}[\]|\\:";'<>?/~`!]/;
        const hasSpecialChars = specialCharsRegex.test(value);

        if (hasSpecialChars) {
          // Show error message for special characters
          const fieldName =
            key === "pickupAddress"
              ? "Pickup Address"
              : key === "landmark"
              ? "Landmark"
              : "City";
          setValidationErrors((prev) => ({
            ...prev,
            [key]: `${fieldName} cannot contain special characters like &, @, #, $, %, ^, *, etc.`,
          }));

          // Remove special characters and update the field
          const sanitizedValue = value.replace(specialCharsRegex, "");
          setFormData((prev) => ({ ...prev, [key]: sanitizedValue }));

          // Show alert for immediate feedback
          alert(
            `Special characters are not allowed in ${fieldName}. They have been removed automatically.`
          );
        } else {
          setFormData((prev) => ({ ...prev, [key]: value }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [key]: value }));
      }
    },
    [alert]
  );

  const stateOptions = useMemo(() => {
    return states.map((s) => ({ label: s, value: s }));
  }, [states]);
  //handle pincode
  const handlePincodeChange = useCallback(async (pincode) => {
    const numeric = pincode.replace(/\D/g, "");
    handleChange("pincode", numeric);

    if (numeric.length !== 6) return;

    setLoadingPincode(true);

    try {
      // 1️⃣ Fetch using api.postalpincode.in
      const res = await fetch(
        `https://api.postalpincode.in/pincode/${numeric}`
      );
      const data = await res.json();

      if (
        data[0]?.Status !== "Success" ||
        !data[0]?.PostOffice ||
        data[0].PostOffice.length === 0
      ) {
        alert("Pincode not found. Please enter manually.");
        return;
      }

      const office = data[0].PostOffice[0];

      const detectedCity = office.District;
      const detectedState = office.State; // Full state name

      // 2️⃣ Set city + state
      handleChange("city", detectedCity);
      handleChange("selectedState", detectedState);
    } catch (err) {
      console.error("Pincode lookup failed:", err);
    } finally {
      setLoadingPincode(false);
    }
  }, []);

  useEffect(() => {
    if (!formData.selectedState) return;

    const fetchCities = async () => {
      try {
        const res = await fetch(
          "https://countriesnow.space/api/v0.1/countries/state/cities",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              country: "India",
              state: formData.selectedState,
            }),
          }
        );

        const data = await res.json();
        if (data.data) {
          setCities(data.data.sort());
        }
      } catch (e) {
        console.error("Error fetching cities:", e);
      }
    };

    fetchCities();
  }, [formData.selectedState]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await fetch(
          "https://countriesnow.space/api/v0.1/countries/states",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ country: "India" }),
          }
        );

        const data = await res.json();
        if (data.data?.states) {
          const names = data.data.states.map((s) => s.name).sort();
          setStates(names);
        }
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    };

    fetchStates();
  }, []);

  return (
    <div className="flex flex-col gap-2 mt-4">
      {generalError && (
        <p className="text-red-500 text-xs pl-2">{generalError}</p>
      )}
      {/* Contact Person */}
      <InputField
        label="Contact Person"
        placeholder="Enter Contact Person"
        name="contactPerson"
        type="text"
        value={formData.contactPerson}
        onChange={(val) => handleChange("contactPerson", val)}
        error={getFieldError("contactPerson")}
      />

      {/* Mobile No */}
      <InputField
        label="Mobile No."
        placeholder="Enter Mobile No."
        name="mobileNo"
        type="number"
        value={formData.mobileNo}
        onChange={(val) => handleChange("mobileNo", val)}
        error={getFieldError("contactPhone")}
      />

      {/* Pincode and City */}
      <div className="flex gap-2">
        <InputField
          label={loadingPincode ? "Pincode (Loading...)" : "Pincode"}
          name="pincode"
          placeholder="Enter Pincode"
          onChange={handlePincodeChange}
          value={formData.pincode}
          disabled={loadingPincode}
          error={getFieldError("pincode")}
          maxLength={6}
        />
        <InputField
          type="text"
          label="City"
          placeholder="Enter City"
          name="city"
          value={formData.city}
          onChange={(value) => handleChange("city", value)}
          error={getFieldError("city")}
        />
      </div>

      {/* State Dropdown */}
      <DropDownInput
        label="State"
        placeholder="Select State"
        name="state"
        options={stateOptions}
        onChange={(val) => handleChange("selectedState", val)}
        value={formData.selectedState}
      />

      {/* Pickup Address */}
      <AddressInputField
        label="Pickup Address"
        placeholder="Enter Pickup Address"
        name="pickupAddress"
        value={formData.pickupAddress}
        onChange={(val) => handleChange("pickupAddress", val)}
        error={
          getFieldError("pickupAddress") || getErrorMessage("lines.0.line1")
        }
      />

      {/* Landmark */}
      <InputField
        label="Landmark"
        placeholder="Enter Landmark"
        name="landmark"
        type="text"
        value={formData.landmark}
        onChange={(val) => handleChange("landmark", val)}
        error={getFieldError("landmark")}
      />

      {/* GST Number */}
      <InputField
        label="GST NO."
        placeholder="Enter GST NO."
        name="gstNo"
        type="text"
        value={formData.gstNo}
        onChange={(val) => handleChange("gstNo", val)}
        error={getFieldError("gstNumber")}
      />

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <SheetClose asChild>
          <Button variant="outline" size="sm" className="mt-4">
            Close
          </Button>
        </SheetClose>
        <Button
          type="submit"
          onClick={isEdit ? handleEdit : handleSubmit}
          variant="default"
          icon={FaArrowRight}
          size="sm"
          className="mt-4"
        >
          {isEdit ? "Update" : "Save"}
        </Button>
      </div>
      <AlertDialog />
    </div>
  );
};

export default PickupAddressForm;
